import { capitalCase } from 'capital-case'
import { INSIGHT_WIDGET_LOCKED_CLASS, INSIGHT_WIDGET_PRINTABLE_CLASS } from 'src/atoms/report-insight'
import { InsightWidget } from 'src/types/report-insights'
import { HIDE_WHEN_PRINT, getNoPrintElements, saveHTMLToClippedImageData } from 'src/utils/html-to-image'

export const getPrintableInsightWidgets = (): InsightWidget[] => {
  const nodes = document.querySelectorAll(`.${INSIGHT_WIDGET_PRINTABLE_CLASS}`)
  if (!nodes.length) {
    return []
  }
  const widgets: InsightWidget[] = []
  nodes.forEach((node) => {
    const hasPrintableParent = node.parentElement?.closest(`.${INSIGHT_WIDGET_PRINTABLE_CLASS}`)
    if (hasPrintableParent) {
      return
    }
    const id = node.getAttribute('id')
    if (!id) {
      return
    }
    const image = undefined

    let label = id

    const type = id.startsWith('text-')
      ? 'text'
      : id.startsWith('image-')
        ? 'image'
        : id.startsWith('map-')
          ? 'map'
          : 'alphamap'

    // Extract the label from the id
    if (type === 'text') {
      label = label.replace('text-', '')
    }
    if (type === 'image') {
      label = label.replace('image-', '').split('_').slice(0, -1).join('_')
    }
    if (type === 'map') {
      label = label.replace('map-', '')
    }

    // Pretty print the label
    label = capitalCase(label)
    // Uppercase Aadt to AADT
    if (label.includes('Aadt')) {
      label = label.replace('Aadt', 'AADT')
    }

    widgets.push({
      id,
      image,
      label,
      visible: true,
      type: type,
    })
  })
  return widgets
}

export const getLockedInsightWidgets = (): Partial<InsightWidget>[] => {
  const nodes = document.querySelectorAll(`.${INSIGHT_WIDGET_LOCKED_CLASS}`)
  if (!nodes.length) {
    return []
  }
  const widgets: Partial<InsightWidget>[] = []
  nodes.forEach((node) => {
    const hasLockedParent = node.parentElement?.closest(`.${INSIGHT_WIDGET_LOCKED_CLASS}`)
    if (hasLockedParent) {
      return
    }
    const id = node.getAttribute('id')
    if (!id) {
      return
    }

    const label = determineInsightWidgetLabel(id)

    widgets.push({ id, label })
  })
  return widgets
}

export const determineInsightWidgetLabel = (id: string): string => {
  let label = capitalCase(id)
  // Uppercase Aadt to AADT
  if (label.includes('Aadt')) {
    label = label.replace('Aadt', 'AADT')
  }
  const type = id.startsWith('text-')
    ? 'text'
    : id.startsWith('image-')
      ? 'image'
      : id.startsWith('map-')
        ? 'map'
        : 'alphamap'
  if (type === 'text') {
    label = label.replace('Text', '')
  }
  if (type === 'image') {
    label = label.replace('Image', '')
  }
  if (type === 'map') {
    label = label.replace('Map', '')
  }
  return label
}

export const getPrintableMapWidget = async (
  id: string,
  clippedArea: {
    height: number
    width: number
    x: number
    y: number
  }
) => {
  const widget = getMapWidget(id)
  if (!widget) {
    return undefined
  }

  // Get the clipped image
  const image = await saveHTMLToClippedImageData(id, clippedArea)
  widget.image = image

  return widget
}

export const getMapWidget = (id: string) => {
  const node = document.getElementById(id)
  if (!node) {
    return undefined
  }

  const noPrintElements = getNoPrintElements(node)
  // Hide no-print elments
  noPrintElements.forEach((element) => {
    element.classList.add(HIDE_WHEN_PRINT)
  })

  // Unhide no-print elments
  noPrintElements.forEach((element) => {
    element.classList.remove(HIDE_WHEN_PRINT)
  })

  const widget: InsightWidget = {
    id,
    label: 'Map',
    visible: true,
    type: 'map',
    featureCollection: undefined,
  }
  return widget
}
